import { LOOKUP_TABLES, LookupTableName } from '../interfaces/lookup.interfaces';

/**
 * V2 §12 controlled values — Product Creation / catalogues.
 * Dropdowns must load from LookupService / LOOKUP_TABLES, not hardcoded UI lists.
 */

/** Short option-map key used by typed-config / FPT cards (`LookupFoo` → `Foo`). */
export function lookupOptionKey(tableName: LookupTableName | string): string {
  return String(tableName).replace(/^Lookup/, '');
}

/**
 * Lookup tables that drive Product Creation typed fields
 * (period/cadence/scope/operator-adjacent timing, ranking, perfection, etc.).
 */
export const V2_PRODUCT_CREATION_LOOKUP_TABLES = [
  LOOKUP_TABLES.ConditionOccurrences,
  LOOKUP_TABLES.TimingRelationships,
  LOOKUP_TABLES.TimeUnits,
  LOOKUP_TABLES.ConditionCadences,
  LOOKUP_TABLES.MeasurementScopes,
  LOOKUP_TABLES.AssessmentAnchorTypes,
  LOOKUP_TABLES.ThresholdValueTypes,
  LOOKUP_TABLES.ConditionTriggerEvents,
  LOOKUP_TABLES.RankingRequirements,
  LOOKUP_TABLES.PerfectionMethods,
  LOOKUP_TABLES.CollateralAssetTypes,
  LOOKUP_TABLES.SecuritySatisfactionRules,
] as const satisfies readonly LookupTableName[];

/** Catalogue / document controlled tables for provision & signature (and related). */
export const V2_DOCUMENT_CONTROLLED_LOOKUP_TABLES = [
  LOOKUP_TABLES.DocumentProvisionMethods,
  LOOKUP_TABLES.DocumentSignatureRequirements,
  LOOKUP_TABLES.DocumentVerificationMethods,
  LOOKUP_TABLES.DocumentOriginalRequirements,
  LOOKUP_TABLES.DocumentRevisionPolicies,
] as const satisfies readonly LookupTableName[];

/** Covenant catalogue controlled tables (ids via LookupService helpers). */
export const V2_COVENANT_CATALOGUE_LOOKUP_TABLES = [
  LOOKUP_TABLES.CovenantMeasurementTypes,
  LOOKUP_TABLES.CovenantOperators,
  LOOKUP_TABLES.CovenantUnitTypes,
  LOOKUP_TABLES.CovenantEvidenceTypes,
  LOOKUP_TABLES.CovenantVerificationMethods,
  LOOKUP_TABLES.CovenantCalculationProfiles,
] as const satisfies readonly LookupTableName[];

/**
 * Expected V2 aliases after lookup load (API may add more; FE must not invent others).
 * Used by unit tests — not for rendering dropdowns.
 */
export const V2_EXPECTED_ALIASES = {
  CovenantOperators: [
    'Equal',
    'Not Equal',
    'Greater Than',
    'Greater Than Or Equal',
    'Less Than',
    'Less Than Or Equal',
    'Between',
    'Not Between',
    'On Or Before',
  ] as const,
  CovenantMeasurementTypes: [
    'ratio',
    'calculated',
    'amount',
    'boolean',
    'count',
    'percentage',
    'qualitative',
  ] as const,
  /** API stores calculationProfile as string (not lookup id) — keep as controlled alias set. */
  CovenantCalculationProfiles: [
    'None',
    'Trailing Twelve Months',
    'As Of Date',
    'Average',
    'Period Start',
    'Period End',
    'Month to Date',
    'Quarter to Date',
    'Year to Date',
    'Sum/Cumulative',
    'Minimum',
    'Maximum',
    'Annualized',
  ] as const,
  RequirementModes: ['REQUIRED', 'OPTIONAL'] as const,
} as const;

/**
 * Offline fallback for the string-backed calculationProfile.
 *
 * The dropdown now loads from dbo.LookupCovenantCalculationProfiles via
 * LookupService.getCovenantCalculationProfiles(); these values are used only when that table is
 * missing (2026-09-07_AddCovenantCalculationProfileLookup.sql not yet run) so a required field is
 * never left empty. They are the seeded aliases, so the two lists agree.
 *
 * CUSTOM is absent by design — retired on 2026-09-07 because its behaviour is undefined. Covenants
 * still holding it keep the value; the add/edit form re-adds it as a one-off option so an unrelated
 * edit to such a covenant is not blocked.
 */
export const V2_CALCULATION_PROFILE_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'None', value: 'None' },
  { label: 'Trailing Twelve Months', value: 'Trailing Twelve Months' },
  { label: 'As Of Date', value: 'As Of Date' },
  { label: 'Average', value: 'Average' },
  { label: 'Period Start', value: 'Period Start' },
  { label: 'Period End', value: 'Period End' },
  { label: 'Month to Date', value: 'Month to Date' },
  { label: 'Quarter to Date', value: 'Quarter to Date' },
  { label: 'Year to Date', value: 'Year to Date' },
  { label: 'Sum/Cumulative', value: 'Sum/Cumulative' },
  { label: 'Minimum', value: 'Minimum' },
  { label: 'Maximum', value: 'Maximum' },
  { label: 'Annualized', value: 'Annualized' },
];

/**
 * Default profile for a covenant that has none. A real seeded row, not the absence of a value —
 * the form requires a selection — so the list column and the form agree on the same alias.
 */
export const V2_CALCULATION_PROFILE_DEFAULT = 'None';

/** Domain → LOOKUP_TABLES mapping for V2 §12 (period/cadence/scope/operator/ranking/perfection/provision/signature). */
export const V2_CONTROLLED_VALUE_DOMAINS = {
  period: LOOKUP_TABLES.TimeUnits,
  cadence: LOOKUP_TABLES.ConditionCadences,
  scope: LOOKUP_TABLES.MeasurementScopes,
  operator: LOOKUP_TABLES.CovenantOperators,
  ranking: LOOKUP_TABLES.RankingRequirements,
  perfection: LOOKUP_TABLES.PerfectionMethods,
  provision: LOOKUP_TABLES.DocumentProvisionMethods,
  signature: LOOKUP_TABLES.DocumentSignatureRequirements,
} as const;
